
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { MqResponsive, updateBreakpoints } from 'vue3-mq'

import AsHeaderMobile from '@web/components/headers/AsHeaderMobile.vue'
import AsCourseTitle from '@web/components/AsCourseTitle.vue'
import AsCourseProgress from '@web/components/AsCourseProgress.vue'
import AsCourseTabs from '@web/components/AsCourseTabs.vue'
import AsWorkoutList from '@web/components/AsWorkoutList.vue'
import AsButtonChat from '@web/components/AsButtonChat.vue'
import AsSettingModal from '@web/components/AsSettingModal.vue'
import AsRow from '@web/components/AsRow.vue'
import AsWarning from '@web/components/AsWarning.vue'
import AsMenu from '@web/components/AsMenu.vue'
import AsHeaderMain from '@web/components/headers/AsHeaderMain.vue'
import AsFooter from '@web/components/AsFooter.vue'
import AsSpacer from '@web/components/AsSpacer.vue'
import AsBreadcrumb from '@web/components/AsBreadcrumb.vue'
import AsPreloader from '@web/components/AsPreloader.vue'
import AsLesson from '@web/components/AsLesson.vue'
import AsHomeworkItem from '@web/components/AsHomeworkItem.vue'
import AsDocuments from '@web/components/AsDocuments.vue'
import AsHeaderVideo from '@web/components/headers/AsHeaderVideo.vue'
import AsBanner from '@web/components/AsBanner.vue'
import AsVideosPlayerJs from '@web/components/AsVideosPlayerJs.vue'
import AsDayTabs from '@web/components/AsDayTabs.vue'
import AsEmpty from '@web/components/AsEmpty.vue'
import AsAlertRenewCourseMobile from '@web/components/AsAlertRenewCourseMobile.vue'
import AsBannersTop from '@web/components/AsBannersTop.vue'
import AsBannerInsteadVideo from '@web/components/AsBannerInsteadVideo.vue'

import useDay from '@web/composition/useDay'

import i18n from '@web/plugins/i18n'

import { getHomeworkStatus } from '@web/common/HomeworkStatuses'
import { prettyHoursMinutes, performanceEnd } from '@web/common/Utils'
import Analytics from '@web/services/Analytics/Analytics'
import sentry from '@web/common/sentry'
import Logger from '@web/common/Logger'
import Swal from 'sweetalert2'

import ConstHomeworkStatus from '@web/consts/HomeworkStatus'
import DayStatus from '@web/consts/DayStatus'

import { useStore } from '@web/store'
import { useRouter, useRoute } from 'vue-router'
import StreamStatus from '@web/consts/StreamStatus'
import FirebasePerformance from '@web/services/firebasePerformance'
import { useMeta } from '@web/common/vueMeta'

export default defineComponent({
  components: {
    AsBannerInsteadVideo,
    AsBannersTop,
    AsAlertRenewCourseMobile,
    MqResponsive,
    AsHeaderMobile,
    AsHeaderVideo,
    AsCourseTitle,
    AsCourseProgress,
    AsCourseTabs,
    AsWorkoutList,
    AsButtonChat,
    AsSettingModal,
    AsRow,
    AsWarning,
    AsMenu,
    AsHeaderMain,
    AsFooter,
    AsSpacer,
    AsBreadcrumb,
    AsPreloader,
    AsLesson,
    AsHomeworkItem,
    AsDocuments,
    AsBanner,
    AsVideosPlayerJs,
    AsDayTabs,
    AsEmpty
  },
  setup () {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    const {
      homeworks,
      stream,
      streamId,
      filteredLessons,
      currentLesson,
      day,
      dayId,
      stageId,
      setAltDayIdAndAltStageId,
      onOpenDay,
      dayStatus,
      onFinishDay,
      progress,
      checkNecessaryParameters,
      currentLessonIndex,
      course,
      sendStatistic,
      title,
      bannerInsteadVideo,
      bannerMobileInsteadVideo,
      ...data
    } = useDay({ route, router })

    useMeta({ title })

    const courseId = computed(() => stream.value?.courseId)
    const loaded = ref(false)
    const nextDayLoaded = ref(true)
    const firstLoaded = ref(false)
    const resizeColumnsTimer = ref(null)
    const playPlayer = ref(false)
    const isOpenMenu = ref(false)

    const autoPlayNextVideo = computed(() => {
      if (!bannerInsteadVideo.value) {
        return true
      }
      return playPlayer.value
    })
    const autoPlayNextVideoMobile = computed(() => {
      if (!bannerMobileInsteadVideo.value) {
        return true
      }
      return playPlayer.value
    })

    const showWarningStep = computed(() => {
      const { status } = getHomeworkStatus(store.getters['homework/getHomeworkStatusesByDayIdAndStreamId'](dayId.value, streamId.value))
      // TODO Пока уберем эту проверку надо разобратся как реализовать на бэке
      // return homeworks.value.filter(h => h.dayHomework?.isRequired)?.length > 0 && status === ConstHomeworkStatus.NOT_UPLOADED
      return homeworks.value.length > 0 && status === ConstHomeworkStatus.NOT_UPLOADED
    })
    const tabs = computed(() => {
      const _tabs = [
        {
          name: 'lessons',
          text: i18n.global.t('lesson')
        }
      ]
      if (homeworks.value.length > 0) {
        _tabs.push({
          name: 'homework',
          text: i18n.global.t('homework')
        })
      }
      if (course.value?.documents && course.value.documents.length > 0) {
        _tabs.push({
          name: 'documents',
          text: i18n.global.t('documents')
        })
      }
      return _tabs
    })
    const currentTab = computed(() => {
      const index: string = router.currentRoute.value.query.tab ? router.currentRoute.value.query.tab.toString() : 'lessons'
      return {
        lessons: 'lessons',
        homework: 'homework',
        documents: 'documents'
      }[index]
    })

    async function fetchData () {
      const stream = await store.dispatch('stream/fetchUserCourseStreamCached', { id: streamId.value })
      if (stream.status === StreamStatus.ENDED || stream.status === StreamStatus.STOPPED) {
        router.push({ name: 'courses' })
        return
      }

      await store.dispatch('course/fetchCourse', { id: stream.courseId, requestLessons: true })

      await store.dispatch('stream/fetchStatuses', {
        id: streamId.value,
        expand: 'stages,days,homeworks'
      })

      if (!dayId.value || !stageId.value) {
        await store.dispatch('stage/fetchStages', { courseId: stream.courseId })
        setAltDayIdAndAltStageId()
      }

      await store.dispatch('day/fetchDayCached', { courseId: stream.courseId, stageId: stageId.value, dayId: dayId.value })

      await onOpenDay()
    }

    function resizeColumns () {
      if (window.innerWidth >= 1244) {
        const sidebarNode: HTMLElement | null = document.querySelector('.asRow_col.-sidebar .-lessons')
        const contentNode: HTMLElement | null = document.querySelector('.js-day-content')

        if (sidebarNode && contentNode) {
          const contentHeight = contentNode.offsetHeight
          sidebarNode.style.maxHeight = `${contentHeight}px`
        }
      }
    }
    function showErrorAlert () {
      Swal.fire({
        title: i18n.global.t('error.error'),
        text: i18n.global.t('error.day'),
        icon: 'error'
      })
    }

    function selectLesson (index: number) {
      currentLessonIndex.value = index
    }

    async function init () {
      let dayLoading = performance.now()
      const startLoading = Date.now()
      try {
        loaded.value = false

        await fetchData()
        sendStatistic()

        setTimeout(() => nextTick(resizeColumns), 1000)
      } catch (e) {
        if (['day', 'day_homework'].includes(String(router.currentRoute.value.name) || '')) {
          sentry.captureException(e)
          Logger.error(e)
          showErrorAlert()
        }
      } finally {
        dayLoading = performanceEnd(dayLoading)
        Logger.info(`[WPO] Day loaded: ${dayLoading} ms`)
        Analytics.send({
          category: 'ux.performance',
          action: 'load_day',
          label: dayLoading.toString()
        })
        loaded.value = true
        firstLoaded.value = true
        checkNecessaryParameters()
        selectLesson(0)

        FirebasePerformance.record({
          traceName: 'load_day',
          startTime: startLoading,
          options: {
            attributes: {
              userCourseStreamId: streamId.toString(),
              streamId: (stream.value?.streamId || '').toString(),
              courseId: (course.value?.id || '').toString(),
              courseTitle: course.value?.title || '',
              dayId: dayId.value.toString(),
              stageId: stageId.value.toString()
            }
          }
        })
      }
    }

    onMounted(() => {
      updateBreakpoints({
        preset: 'bootstrap5',
        breakpoints: {
          mobile: 0,
          tablet: 414 * 2,
          desktop: 940 + 12 * 2 + 14 * 2
        }
      })

      init()
    })

    watch(filteredLessons, () => {
      nextTick(resizeColumns)
    })

    watch(() => dayStatus.value?.id, async (dayStatusId: number | undefined, prev: number | undefined) => {
      if (dayStatusId && stream.value && prev) {
        nextDayLoaded.value = false
        const sId = streamId.value
        await store.dispatch('day/fetchDayCached', { courseId: stream.value.courseId, stageId: stageId.value, dayId: dayId.value })
        await store.dispatch('day/fetchProgress', { streamId: sId, dayStatusId })
        nextDayLoaded.value = true
      }
    })

    watch(progress, () => {
      if ((dayStatus.value ? dayStatus.value.toString() : '') === DayStatus.ENDED) {
        return
      }
      if (progress.value === 100) {
        onFinishDay()
          .then(() => store.dispatch('stream/fetchStatuses', { id: streamId.value, expand: 'stages,days,homeworks' }))
      }
    })

    watch(() => router.currentRoute.value.params.dayNumber, dayNumber => {
      if (dayNumber) {
        setAltDayIdAndAltStageId()
      }
    })

    onBeforeUnmount(() => {
      updateBreakpoints({
        preset: 'bootstrap5',
        breakpoints: {
          mobile: 0,
          tablet: 414 * 2,
          desktop: 1192 + 12 * 2 + 14 * 2
        }
      })
      clearInterval(resizeColumnsTimer.value || 0)
    })

    const courseTitle = computed(() => {
      return course.value?.title || ''
    })

    const isShowDocuments = computed<boolean>(() => {
      if (!course.value) {
        return false
      }
      return (course.value.documents && course.value.documents.length > 0) || !!course.value.instructionUrl
    })

    const showBtnChat = computed(() => course.value?.isExpertMessageEnable)

    watch(currentTab, () => {
      if (route.name === 'day') {
        checkNecessaryParameters()
      }
    }, {
      immediate: false
    })

    return {
      ...data,
      bannerInsteadVideo,
      bannerMobileInsteadVideo,
      playPlayer,
      autoPlayNextVideo,
      autoPlayNextVideoMobile,
      title,
      loaded,
      nextDayLoaded,
      isOpenMenu,
      menu: computed(() => store.getters['menu/general']),
      course,
      day,
      stream,
      progress,
      currentLesson,
      currentLessonIndex,
      currentTab,
      tabs,
      filteredLessons,
      homeworks,
      showWarningStep,
      courseTitle,
      selectLesson,
      streamId,
      courseId,
      dayId,
      prettyHoursMinutes,
      isShowDocuments,
      showBtnChat
    }
  }
})
